<template>
  <div :class="['verification', !isTrialFlow || 'short']">
    <v-form ref="phone">
       <v-col cols="12" md="6" data-attr="country">
        <autocomplete
            v-model="params.country"
            :label="$t('auth.country')"
            :item-text="`translated_names[${lang.toLowerCase()}]`"
            outlined
            ref="country-select"
            :items="countryList"
            class="country-select"
            :attach="attachSelector"
            :rules="rulesCountry"
            return-object
            :noDataText="$t('noDataAvailable')"
            @input="updatePhoneValidate()"
        >
          <template slot="append">
            <v-tooltip right max-width="330px" :debounce="200">
              <template v-slot:activator="{ on }">
                <v-btn
                    icon
                    v-on="on"
                    slot="activator"
                    tag="span"
                    fab
                    @click.stop="$event.preventDefault()"
                    class="input-help"
                    :ripple="false"
                >
                  <v-icon class="sr-icon-help"/>
                </v-btn>
              </template>
              <span>{{ $t('auth.countryHint') }}</span>
            </v-tooltip>
            <div class="v-input__icon v-input__icon--append">
              <span aria-hidden="true" class="v-icon notranslate theme--light"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" class="v-icon__svg"><path d="M7,10L12,15L17,10H7Z"></path></svg></span>
            </div>
          </template>
        </autocomplete>
      </v-col>

      <template v-if="!isTrialFlow && !isMultiDomainFlow">
        <v-col class="order-check" v-if="flow !== $options.FLOW.DOMAIN" cols="12" md="6" data-attr="order-check">
          <v-select
              v-model="params.verifytype"
              :label="$t('auth.verifyType')"
              outlined
              ref="check-select"
              :items="verifyTypesList"
              :rules="rulesVerifyType"
              :append-outer-icon="countryUa ? 'sr-icon-help' : undefined"
              @click:append-outer="showOrderInfo = true"
              :noDataText="$t('noDataAvailable')"
              :menu-props="{ bottom: true, offsetY: true }"
          />
        </v-col>

        <v-col cols="12" md="6" data-attr="phone">
          <v-text-field
              outlined
              :label="$t('auth.phone')"
              v-model="params.phonenumber"
              v-mask="phoneMask"
              :placeholder="phonePlaceholder"
              :prefix="!!params.country ? params.country.phonecode : undefined"
              :rules="rulesPhoneNumber"
              class="phone-input"
              :loading="loading"
              ref="phone"
              @keydown.enter.prevent="sendSms()"
              validate-on-blur
          />
        </v-col>
        <v-fade-transition>
          <v-col cols="12" md="6" v-if="flow !== $options.FLOW.DOMAIN && params.verifytype === 'sms'">
            <div class="code-holder">
              <v-btn
                  :ripple="false"
                  @click="sendSms"
                  :loading="loading"
                  color="primary"
                  data-attr="sms-code-btn"
                  :disabled="timerCount > 0"
              >{{ $t('auth.getSmsCode') }}</v-btn>
            </div>
          </v-col>
        </v-fade-transition>

        <v-fade-transition>
          <v-col cols="12" v-if="phoneValidation !== undefined || timerCount > 0">
            <div :class="['info-message', phoneValidation === 'valid' ? 'valid' : 'invalid', 'py-0']" :data-attr="`phone-${smsCodeValidation}`">
              <template v-if="phoneValidation">
                <v-icon v-if="phoneValidation === 'valid'" class="sr-icon-check"/>
                <v-icon v-else class="sr-icon-close"/>
                <span v-html="$t(`validation.phone.${phoneValidation}`)"></span>
              </template>
              <p v-if="timerCount > 0" v-html="$t('validation.phoneResend', {time: timerCount})"></p>
            </div>
          </v-col>
        </v-fade-transition>
      </template>
    </v-form>

    <template v-if="!isTrialFlow && !isMultiDomainFlow">
      <v-fade-transition>
        <v-form>
          <v-row v-if="params.verifytype === 'sms' && phoneValidation === 'valid'">
            <v-col cols="12" md="6">
              <div class="code-holder">
                <div class="text-field" data-attr="reg-sms-code">
                  <v-form ref="smsCode" @submit.prevent="verifySmsCode">
                    <v-text-field
                        placeholder="000 000"
                        v-model.trim="smsCode"
                        v-mask="'### ###'"
                        :label="$t('auth.smsCode')"
                        :rules="rulesSmsCode"
                        outlined
                        clearable
                        :readonly="smsCodeValidation === 'valid'"
                    >
                      <template v-slot:message="{ message }">
                        <span v-html="message"></span>
                      </template>
                    </v-text-field>
                  </v-form>
                </div>

                <v-btn
                    :ripple="false"
                    @click="verifySmsCode"
                    :loading="loadingVerifySms"
                    color="primary"
                    data-attr="verify-sms-code-btn"
                    :disabled="smsCodeValidation === 'valid'"
                >{{ $t('auth.apply') }}</v-btn>
              </div>
            </v-col>

            <v-col cols="12" v-if="smsCodeValidation">
              <div :class="['info-message', smsCodeValidation, 'py-0']" :data-attr="`sms-code-${smsCodeValidation}`">
                <v-icon v-if="smsCodeValidation === 'valid'" class="sr-icon-check"/>
                <v-icon v-else class="sr-icon-close"/>
                <span v-html="$t(`validation.smsCode.${smsCodeValidation}`)"></span>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-fade-transition>

      <v-fade-transition>
        <v-row v-if="verificationInvalid">
          <v-col cols="12">
            <div class="info-message invalid py-0" data-attr="verification-invalid">
              <v-icon class="sr-icon-close"/>
              <span v-html="$t('auth.verificationInvalid')"></span>
            </div>
          </v-col>
        </v-row>
      </v-fade-transition>
    </template>

    <dialog-form
        content-class="order-dialog-form"
        v-bind="$attrs"
        v-on="$listeners"
        :show="showOrderInfo"
        @close="showOrderInfo = false"
        :activator="false"
        maxWidth="800"
        closable
        data-attr="reg-dialog"
    >
      <div v-html="$t('auth.testOrderCondition')"></div>
    </dialog-form>
  </div>
</template>

<script>
  import { mapGetters } from "vuex";
  import { SETTINGS_GETTERS, SETTINGS_MUTATIONS } from "@/store/modules/settings";
  import { AUTH_MUTATIONS } from "@/store/modules/auth";
  import { Autocomplete } from '@/shared/components/Autocomplete';
  import { COUNTRIES, FLOW } from '@/constants';
  import DialogForm from '@/shared/components/DialogForm/DialogForm.vue';
  import { getGeoip, sendSms, verifySmsCode } from "@/store/api/auth";
  import * as Sentry from "@sentry/vue";

  export default {
    name: "Verification",
    FLOW,
    props: {
      auth: { type: Object, default: () => {} },
      flow: { type: String },
      parentClass: {type: String, required: true,},
    },
    data: () => ({
      loading: false,
      loadingVerifySms: false,
      countryInfo: undefined,
      phonenumber: '',
      phoneValidation: undefined,
      smsCode: '',
      smsCodeValidation: undefined,
      codeMask: '### ###',
      showOrderInfo: false,
      timerCount: 0,
      timerCountAmount: 30,
      verificationInvalid: false,

      params: {
        country: { code: "UA", phonecode: "+380", translated_names: { en: "Ukraine", ua: "Україна", ru: "Украина" }, mask: "(##)###-##-##" },
        phonenumber: undefined,
        verifytype: 'sms'
      },
      geoIp: 'UA'
    }),
    components: {
      Autocomplete,
      DialogForm,
    },
    created() {
      this.getGeoip();
    },
    async mounted() {
      if (!this.lang) { this.lang =  await this.setLang() };
    },
    computed: {
      ...mapGetters('settings', {
        lang: SETTINGS_GETTERS.LANG,
      }),
      countryUa () {
        return this.params.country?.code === "UA";
      },
      countryList() {
        const list = this.flow !== FLOW.TRIAL ? COUNTRIES.LIST : COUNTRIES.LIST.filter(item => COUNTRIES.TRIAL.includes(item.code));
        const sortList = list.sort((a, b) => a.translated_names[this.lang.toLowerCase()].toLowerCase().localeCompare(b.translated_names[this.lang.toLowerCase()].toLowerCase()) );
        return sortList;
      },
      verifyTypesList() {
        const list = [{ value: "sms", text: this.$t('auth.verifyTypesList.sms')} ];
        if(this.countryUa) {
          list.push({ value: "call", text: this.$t('auth.verifyTypesList.call') })
        }
        return list;
      },

      rulesCountry() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v || this.$t('validation.required'));

        return rules;
      },
      rulesPhoneNumber() {
        const rules = this.$attrs.rules || [];
        const mask = this.params.country && this.params.country.mask ? this.params.country.mask : '';
        let format = this.phonePlaceholder;
        let minN = 0;
        let maxN = 0;
        let minLength = mask ? mask.length : 0;

        rules.push(v => !!v || this.$t('validation.required'));

        if(Array.isArray(mask)) {
          minLength = mask.reduce((min,c) => c < min? c : min).length;

          if(mask.length > 2) {
            const arrMaskAmount = [];
            mask.forEach(item => {
              arrMaskAmount.push(item.split("#").length - 1)
            })
            minN = Math.min(...arrMaskAmount);
            maxN = Math.max(...arrMaskAmount);
          } else {
            format = mask.slice(0, -1).join(', ').concat(mask.length > 1 ? ` ${this.$t('or')} ` : '', mask.slice(-1));
            format = this.replaceMask(format);
          }
        }

        if(mask) { rules.push(v => (!v || v.length >= minLength) || (Array.isArray(mask) && mask.length > 2 ? this.$t('validation.phoneFormatInternational', {n: minN, nn: maxN}) : this.$t('validation.phoneFormat', {format}) ) ) };

        return rules;
      },
      phonePlaceholder() {
        const mask = this.params.country && this.params.country.mask ? this.params.country.mask : '';
        return Array.isArray(mask) || !mask ? '' : this.replaceMask(mask);
      },
      phoneMask() {
        let mask = this.params.country && this.params.country.mask ? this.params.country.mask : '###############';

        if(Array.isArray(mask)) {
          mask = mask?.map(item => item.replaceAll("-", " "));
          mask = [...mask];
        } else {
          mask = mask?.replaceAll("-", " ");
        }

        return mask;
      },
      rulesSmsCode() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v || this.$t('validation.requiredSmsCode'));
        rules.push(v => (!v || v.length >= 7) ||  this.$t('validation.smsCodeFormat', {chars: 6}));

        return rules;
      },
      rulesVerifyType() {
        const rules = this.$attrs.rules || [];
        rules.push(v => !!v || this.$t('validation.requiredSmsCode'));

        return rules;
      },
      isTrialFlow() {
        return this.flow === FLOW.TRIAL;
      },
      isMultiDomainFlow() {
        return this.flow === FLOW.MULTI_DOMAIN;
      },
      attachSelector() {
        return `${this.parentClass} .country-select`;
      },
    },
    methods: {
      replaceMask(val) {
        return val.replaceAll("#", "0").replaceAll("-", " ");
      },

      updatePhoneValidate() {
        setTimeout(() => { this.isValidPhone() }, 100)
      },

      isValidPhone() {
        return this.$refs.phone?.validate() || false;
      },

      async sendSms() {
        this.resetData();

        if (!(this.isTrialFlow && this.params.verifytype === 'sms' && this.isValidPhone() && this.timerCount <= 0)) return

        try {
          this.loading = true;
          const result = await sendSms(this.phonenumber);
          this.phoneValidation = 'valid';
          this.timerCount = this.timerCountAmount;
        } catch (error) {
          Sentry.captureException(error);
          this.phoneValidation = error?.data?.message === 'send_retries_limit_reached' || error?.data?.message === 'already_used_for_trial' ? error?.data?.message : 'invalid';
          if(error?.data?.message === 'time_limit_reached') {
            this.timerCount = this.timerCountAmount;
          }
        } finally {
          this.loading = false;
        }
      },

      isValidSms() {
        return this.$refs.smsCode.validate();
      },

      async verifySmsCode() {
        if (!this.isValidSms()) return

        try {
          this.loadingVerifySms = true;
          const smsCode = this.smsCode.replace(/ /g, '');
          const result = await verifySmsCode(this.phonenumber, smsCode);
          this.smsCodeValidation = 'valid';
          this.timerCount = 0;

          this.$store.commit(`auth/${AUTH_MUTATIONS.SET_VERIFY_HASH}`, result.hash);
        } catch (error) {
          Sentry.captureException(error);
          this.smsCodeValidation = 'invalid';
          this.$store.commit(`auth/${AUTH_MUTATIONS.SET_VERIFY_HASH}`, undefined);
        } finally {
          this.loadingVerifySms = false;
        }
      },

      validate() {
        const phoneValidate = this.isValidPhone();
        let formValidate = phoneValidate;

        if(this.params.verifytype === 'sms' && this.flow !== FLOW.DOMAIN && !this.isTrialFlow && !this.isMultiDomainFlow) {
          const smsCodeValidate = this.phoneValidation === 'valid' ? this.isValidSms() : false;
          formValidate = phoneValidate && smsCodeValidate && this.phoneValidation === 'valid' && this.smsCodeValidation === 'valid';

          this.verificationInvalid = !formValidate;
        }
        return formValidate;
      },

      resetData() {
        this.smsCode = '';
        this.phoneValidation = undefined;
        this.smsCodeValidation = undefined;
        this.verificationInvalid = false;
      },

      async getGeoip() {
        this.loading = true;
        try {
          this.geoIp = await getGeoip();
          this.params.country = this.countryList.find(({ code }) => code === this.geoIp);
        } finally {
          this.loading = false;
        }
      },
    },
    watch: {
      "auth": {
        handler(data) {
          this.$nextTick(() => {
            if(data) {
              this.params.country = this.countryList.find(({code}) => code === data.country);
              this.params.phonenumber = data.phonenumber ? data.phonenumber.replace(`${this.params?.country?.phonecode || ''}.`, '') : '';
            } else {
              this.params = {
                country: { code: "UA", phonecode: "+380", translated_names: { en: "Ukraine", ua: "Україна", ru: "Украина" }, mask: "(##)###-##-##" },
                phonenumber: undefined,
              };
              this.params.verifytype = 'sms';
              this.phonenumber = '';

            }
            this.$emit('input', this.params);
          })
        },
        deep: true,
      },
      countryUa(val) {
        if(!val) { this.params.verifytype = "sms" };
      },
      "params": {
        handler() {
          this.phonenumber = this.params.country && this.params.phonenumber ? (`${this.params.country.phonecode}.${this.params.phonenumber}`).replace(/\(| |\)/g, '') : '';
          const data = {
            country: this.params.country ? this.params.country.code : '',
            ...(!this.isTrialFlow && !this.isMultiDomainFlow ? { phonenumber: this.phonenumber, verifytype: this.params.verifytype } : { isTrialFlow: true })
          };
          this.$emit('input', data);

          this.resetData();
        },
        deep: true,
      },
      'params.verifytype': {
        handler(val) {
          if(val === 'call') {
            this.timerCount = 0;
            this.resetData();
            this.$store.commit(`auth/${AUTH_MUTATIONS.SET_VERIFY_HASH}`, undefined);
          }
        },
        deep: true,
      },
      "timerCount": {
        handler(value) {
          if (value >= 1) {
            setTimeout(() => {
              if(this.timerCount >= 1) { this.timerCount -= 1 }
            }, 1000);
          }
        },
        immediate: true
      },
      smsCode() {
        this.smsCodeValidation = undefined;
        this.verificationInvalid = false;
      },
      smsCodeValidation(val) {
        if(val === 'valid') { this.validate() };
      }
    }
  }
</script>

<style lang="scss">
  .verification {
    width: 100%;

    &.short {
      @media (min-width: $desktop) {
        width: 50%;
      }

      [class^="col-"] {
        flex: 1;
        max-width: 100%;
      }
    }

    form {
      display: flex;
      flex-wrap: wrap;
    }

    .row {
      margin: 0;
    }
  }

  .code-holder {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;

    @media (max-width: 960px) {
      max-width: 574px;
    }

    .text-field {
      max-width: calc(100% - 155px);
      width: 100%;
      padding-right: 11px;

      @media (max-width: 960px) {
        max-width: calc(100% - 155px - 11px);
      }
    }

    .v-text-field.v-input {
      width: 100%;
    }

    .v-btn {
      width: 155px;
    }
  }

  .login-block {
    .verification {
      &.short {
        [class^="col-"] {
          max-width: calc(100% + 24px);
          margin: 0 -12px;
          @media (min-width: $desktop) {
            max-width: calc(100% + 12px);
            margin: -60px 0 0 -12px;
          }
        }
      }
    }
  }
</style>

